/**
 * Admin-Shell (P0, docs/KONZEPT-ADMIN-BEREICH.md § "Informationsarchitektur & UI" +
 * "Architektur-Entscheidung" Punkt 3): schlanke Topbar statt Sidebar - der Admin-Bereich hat nur
 * 5 Nav-Ziele (Dashboard/Mandanten/Impersonationen/Audit/Admins), eine Sidebar waere Overkill.
 * Rendert NUR nach erfolgreichem `whoami` (siehe `index.tsx`) - vor der Bestaetigung bleibt die
 * Seite bewusst unmarkiert (Existenz-Orakel-Grundsatz: eine falsche Host-/Auth-Antwort darf nicht
 * schon durch die Wortmarke "verraten", dass hier ein Admin-Bereich existiert).
 *
 * P1 (docs/LOOP-ADMIN-BEREICH.md § P1 "Seiten /audit und /impersonationen"): "Audit" und
 * "Impersonationen" sind jetzt zusaetzlich zu "Mandanten" anklickbar und ueber
 * `useAdminNavigation()` (`hooks/useAdminNavigation.ts`) per Pfad erreichbar (`onNavigate` reicht
 * den Ziel-Pfad an `navigiereZu` in `index.tsx` durch - echter Deep-Link, F5 bleibt auf der
 * Seite). "Admins" bleibt bewusst "kommt noch" deaktiviert: die Seite existiert noch nicht
 * (eigenes, noch offenes P6-Kriterium) - ihr Nav-Punkt wuerde sonst auf eine leere Flaeche
 * fuehren. Aus demselben Grund bleibt "Mandanten/:id" (Detailansicht) bewusst OHNE eigenen Pfad -
 * sie haengt weiterhin an lokalem `selectedTenantId`-State in `index.tsx`, volle
 * Deep-Link-Faehigkeit fuer einen einzelnen Mandanten ist ein spaeteres, hier nicht gefordertes
 * Kriterium (Konzept-Seitenbaum `/mandanten/:id`).
 *
 * P1 (docs/LOOP-ADMIN-BEREICH.md § P1, sechstes Kriterium "Dashboard-Startseite"): "Dashboard"
 * ist jetzt ebenfalls `verfuegbar:true` — `DashboardSeite.tsx` existiert, Ziel-Pfad "/" (der
 * einzige Pfad, den `useAdminNavigation.ts` `parseAdminPfad` auf `seite: "dashboard"` abbildet).
 * Da "/" bisher implizit auf die Mandantenliste zurueckfiel, ist das Dashboard damit der neue
 * Startzustand des gesamten Admin-Bereichs (Konzept-Seitenbaum listet "/" als Dashboard) — siehe
 * `pages/admin/index.tsx` Kopfkommentar fuer die betroffenen Bestandstests.
 *
 * Impersonations-Banner: `ImpersonationBanner` (`ui/ImpersonationBanner.tsx`) wird unveraendert
 * wiederverwendet - inhaltlich passt es 1:1 (fragt serverseitig `/api/admin/impersonation/aktiv`
 * ab, unabhaengig von einer Mandanten-Ansicht). EINZIGE Annahme, die im Admin-Kontext nicht gilt:
 * der Banner ruecht sich ab `lg` per `lg:left-[var(--nav-w)]` neben die Sidebar der normalen
 * AppShell - eine CSS-Variable, die `ui/AppShell.tsx` per `useLayoutEffect` auf
 * `document.documentElement` schreibt. Der Admin-Host mountet `AppShell` nie, `--nav-w` bliebe
 * also undefiniert und `left` wuerde ungueltig (Rueckfall auf den Startwert `auto`) - sichtbar nur
 * am Desktop-Breakpoint, wo das Banner dann nicht mehr die volle Breite einnaehme. Statt
 * `ImpersonationBanner.tsx` fuer den einzigen anderen Aufrufer (die normale App) zu aendern, wird
 * hier per Tailwind-Arbitrary-Property `[--nav-w:0px]` auf dem AdminShell-Wurzelelement dieselbe
 * Variable mit einem fuer den Admin-Bereich passenden Wert (keine Sidebar = 0px) bereitgestellt -
 * CSS-Custom-Properties vererben sich ueber den DOM-Baum, unabhaengig von `position: fixed`.
 *
 * Mobile-Nav-Fix (Antoine, Handy-Meldung "Menue abgeschnitten/fehlt" bei ~375-430px): die Topbar
 * ist EINE flex-Reihe aus Logo-Block (shrink-0), `.tab-scroll`-Nav (flex-1) und adminId-Badge
 * (shrink-0, volle Stack-Auth-UUID in Monospace) - auf schmalen Viewports beanspruchen Logo +
 * Badge zusammen so viel feste Breite, dass fuer die Nav kaum noch Platz bleibt. Loesung kopiert
 * bewusst das etablierte Muster der normalen App-Shell (`ui/AppShell.tsx` Zeile ~811 +
 * `ui/NavListe.tsx`s `MobileNavDrawer`, Zeilen 199-247): Hamburger-Button (`lg:hidden`) oeffnet ein
 * eigenstaendiges, NICHT-Radix Vollbild-Overlay (`AdminMobileNavDrawer` unten) mit denselben
 * NAV_ITEMS; die `.tab-scroll`-Nav UND das adminId-Badge wandern in der Kopfzeile hinter
 * `hidden lg:flex` (Badge bleibt inhaltlich erhalten, nur unten im Drawer sichtbar, damit die
 * Admin-Identitaets-Info auf dem Handy nicht verloren geht). Radix ist im Projekt nur fuer
 * zentrierte Modals etabliert (`ConfirmSlugModal`, `ui/components.tsx`), fuer Off-Canvas-Menues ist
 * das handgebaute `MobileNavDrawer`-Muster der Praezedenzfall - dabei bleibt auch dieser Drawer,
 * nur mit den neutralen Design-Tokens dieser Datei statt AppShells Marken-Sidebar-Farbe
 * `bg-[#4A2511]` (der Admin-Bereich hat bewusst kein Branding, s. Kommentar oben).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Icon, Logo } from "../../ui/components";
import { ImpersonationBanner } from "../../ui/ImpersonationBanner";
import type { AdminSeite } from "../../hooks/useAdminNavigation";

interface AdminNavItem {
  key: AdminSeite;
  label: string;
  /** Ziel-Pfad fuer `onNavigate` (`useAdminNavigation().navigiereZu`) - nur relevant, wenn
   *  `verfuegbar`. */
  pfad: string;
  verfuegbar: boolean;
}

const NAV_ITEMS: AdminNavItem[] = [
  { key: "dashboard", label: "Dashboard", pfad: "/", verfuegbar: true },
  { key: "mandanten", label: "Mandanten", pfad: "/mandanten", verfuegbar: true },
  { key: "impersonationen", label: "Impersonationen", pfad: "/impersonationen", verfuegbar: true },
  { key: "audit", label: "Audit", pfad: "/audit", verfuegbar: true },
  // P6 (Migration 540): AdminsSeite.tsx existiert.
  { key: "admins", label: "Admins", pfad: "/admins", verfuegbar: true },
];

/** Mobiles Vollbild-Ausklapp-Menue der Admin-Topbar (< lg, s. Kopfkommentar "Mobile-Nav-Fix"):
 *  dieselben `NAV_ITEMS` wie die Desktop-Nav (inkl. `disabled`-Zustand fuer "Admins"), zusaetzlich
 *  das adminId-Badge, das in der Kopfzeile ab lg ausgeblendet ist. Struktur/Verhalten kopiert
 *  bewusst `ui/NavListe.tsx`s `MobileNavDrawer` (Vollbild-Overlay, Backdrop schliesst per Klick,
 *  Escape schliesst, safe-area-Padding oben/unten) - hier mit den neutralen Design-Tokens dieser
 *  Datei statt AppShells Marken-Sidebar-Farbe. Schliesst sich nach einem Klick auf einen
 *  aktivierten Nav-Punkt selbst (`onNavigate` UND `onClose`). */
function AdminMobileNavDrawer({
  open,
  onClose,
  aktiveSeite,
  onNavigate,
  adminId,
}: {
  open: boolean;
  onClose: () => void;
  aktiveSeite: AdminSeite;
  onNavigate: (pfad: string) => void;
  adminId: string;
}) {
  useEffect(() => {
    if (!open) return;
    const aufEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", aufEscape);
    return () => document.removeEventListener("keydown", aufEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <button
        type="button"
        aria-label="Menü schließen"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <nav
        aria-label="Admin-Bereich (mobil)"
        className="absolute inset-y-0 left-0 flex w-[82vw] max-w-80 flex-col bg-surface-container-lowest pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl dark:bg-night-bar"
      >
        <div className="flex items-center justify-between border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
          <span className="font-label-md text-[13px] font-bold tracking-[0.18em] text-on-background dark:text-inverse-on-surface">
            MAESTRO Admin
          </span>
          <button
            type="button"
            aria-label="Menü schließen"
            onClick={onClose}
            className="rounded-full p-2 text-secondary hover:bg-surface-container-high hover:text-on-surface dark:text-secondary-fixed-dim dark:hover:bg-white/10"
          >
            <Icon name="close" className="text-xl" />
          </button>
        </div>

        <ul className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
          {NAV_ITEMS.map((item) => {
            const active = item.key === aktiveSeite;
            return (
              <li key={item.key}>
                <button
                  type="button"
                  disabled={!item.verfuegbar}
                  aria-current={active ? "page" : undefined}
                  title={item.verfuegbar ? undefined : "Kommt noch"}
                  onClick={
                    item.verfuegbar
                      ? () => {
                          onNavigate(item.pfad);
                          onClose();
                        }
                      : undefined
                  }
                  className={`flex w-full items-center rounded-[9px] px-3 py-2.5 text-left font-body-sm text-body-sm font-medium transition-colors ${
                    active
                      ? "bg-primary text-on-primary font-semibold"
                      : item.verfuegbar
                        ? "text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/10"
                        : "cursor-not-allowed text-on-surface-variant/50 dark:text-secondary-fixed-dim/50"
                  }`}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-outline-variant/30 px-4 py-3 dark:border-outline/30">
          <span
            title="Deine Platform-Admin-ID"
            className="flex items-center gap-1.5 rounded-full bg-surface-container-high px-3 py-1 font-mono text-[11px] text-on-surface-variant dark:bg-white/[0.06] dark:text-secondary-fixed-dim"
          >
            <Icon name="badge" className="text-[14px]" />
            {adminId}
          </span>
        </div>
      </nav>
    </div>
  );
}

export function AdminShell({
  adminId,
  aktiveSeite,
  onNavigate,
  children,
}: {
  adminId: string;
  /** Welcher Nav-Punkt aktuell hervorgehoben wird (siehe `index.tsx`: aus `useAdminNavigation()`
   *  abgeleitet, auf die heute schon gebauten Ziele eingedampft). */
  aktiveSeite: AdminSeite;
  /** Navigiert ueber `useAdminNavigation().navigiereZu` zum jeweiligen `pfad` des geklickten
   *  Nav-Punkts - der Aufrufer entscheidet, was zusaetzlich zurueckgesetzt werden muss (z. B.
   *  `selectedTenantId` beim Sprung zurueck auf "Mandanten"). */
  onNavigate: (pfad: string) => void;
  children: ReactNode;
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-dvh bg-surface text-on-background dark:bg-night-canvas dark:text-inverse-on-surface [--nav-w:0px]">
      <ImpersonationBanner />
      <header
        className="sticky top-[var(--impersonation-banner-h,0px)] z-30 border-b border-outline-variant/30 bg-surface-container-lowest px-4 dark:border-outline/30 dark:bg-night-bar"
      >
        <div className="mx-auto flex max-w-6xl items-center gap-6">
          <div className="flex shrink-0 items-center gap-2.5 py-3">
            <Logo
              size={26}
              mitWortmarke
              wortmarke="MAESTRO Admin"
              wortmarkeClassName="font-label-md text-[13px] font-bold tracking-[0.18em] text-on-background dark:text-inverse-on-surface"
            />
          </div>

          {/* Hamburger < lg: oeffnet `AdminMobileNavDrawer` (alle 5 NAV_ITEMS + adminId-Badge) -
              s. Kopfkommentar "Mobile-Nav-Fix". Ab lg unsichtbar, die Desktop-Nav rechts daneben
              uebernimmt. */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Admin-Navigation öffnen"
            className="shrink-0 rounded-full p-2 text-secondary transition-colors hover:bg-surface-container-high hover:text-primary dark:text-secondary-fixed-dim dark:hover:bg-white/10 lg:hidden"
          >
            <Icon name="menu" className="text-xl" />
          </button>

          <nav aria-label="Admin-Bereich" className="tab-scroll hidden flex-1 gap-1 self-stretch lg:flex">
            {NAV_ITEMS.map((item) => {
              const active = item.key === aktiveSeite;
              return (
                <button
                  key={item.key}
                  type="button"
                  disabled={!item.verfuegbar}
                  aria-current={active ? "page" : undefined}
                  title={item.verfuegbar ? undefined : "Kommt noch"}
                  onClick={item.verfuegbar ? () => onNavigate(item.pfad) : undefined}
                  className={`whitespace-nowrap border-b-2 px-3 py-3 font-label-md text-label-md transition-colors ${
                    active
                      ? "border-primary text-primary dark:border-primary-fixed dark:text-primary-fixed"
                      : item.verfuegbar
                        ? "border-transparent text-secondary hover:text-on-surface dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
                        : "cursor-not-allowed border-transparent text-on-surface-variant/50 dark:text-secondary-fixed-dim/50"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Ab lg wie bisher in der Kopfzeile sichtbar; auf schmalen Viewports raus (s.
              Kopfkommentar) - derselbe Inhalt erscheint stattdessen unten im Mobile-Drawer,
              damit die Admin-Identitaets-Info nicht verloren geht. */}
          <span
            title="Deine Platform-Admin-ID"
            className="my-2.5 hidden shrink-0 items-center gap-1.5 rounded-full bg-surface-container-high px-3 py-1 font-mono text-[11px] text-on-surface-variant dark:bg-white/[0.06] dark:text-secondary-fixed-dim lg:flex"
          >
            <Icon name="badge" className="text-[14px]" />
            {adminId}
          </span>
        </div>
      </header>

      <AdminMobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        aktiveSeite={aktiveSeite}
        onNavigate={onNavigate}
        adminId={adminId}
      />

      <main className="mx-auto max-w-6xl px-4 py-8">{children}</main>
    </div>
  );
}
